import { useMemo, useState } from "react";
import {
  CircleAlert,
  Check,
  ChevronRight,
  Clock3,
  Flame,
  Headphones,
  LoaderCircle,
  Play,
  WandSparkles
} from "lucide-react";
import { Brand } from "./Brand";
import {
  allLessons,
  courseSections,
  pilotLessons,
  type Lesson
} from "../data/curriculum";
import {
  readCompletedLessonIds,
  readLessonPosition
} from "../lib/courseProgress";

type CourseHomeProps = {
  onOpenLesson: (lesson: Lesson) => void;
  generatedLessonIds: ReadonlySet<number>;
  buildStatuses: Record<number, LessonBuildStatus>;
  hasGenerationAccess: boolean;
  activeGenerationId: number | null;
  onGenerateLesson: (lessonId: number, token?: string) => Promise<void>;
};

export type LessonBuildStatus = {
  state: "checking" | "queued" | "rendering" | "ready" | "error";
  completed?: number;
  total?: number | null;
  message?: string;
};

const previewLessonCount = 10;

export function CourseHome({
  onOpenLesson,
  generatedLessonIds,
  buildStatuses,
  hasGenerationAccess,
  activeGenerationId,
  onGenerateLesson
}: CourseHomeProps) {
  const completedLessonIds = useMemo(() => readCompletedLessonIds(), []);
  const availableLessonIds = useMemo(
    () =>
      new Set([
        ...pilotLessons.map(lesson => lesson.id),
        ...generatedLessonIds
      ]),
    [generatedLessonIds]
  );
  const nextCourseLesson =
    allLessons.find(lesson => !completedLessonIds.has(lesson.id)) ??
    allLessons.at(-1)!;
  const [selectedSectionId, setSelectedSectionId] = useState(
    nextCourseLesson.sectionId
  );
  const [showAllLessons, setShowAllLessons] = useState(false);
  const [pendingTokenLessonId, setPendingTokenLessonId] = useState<
    number | null
  >(null);
  const [tokenInput, setTokenInput] = useState("");
  const selectedSection =
    courseSections.find(section => section.id === selectedSectionId) ??
    courseSections[0];
  const selectedLessons = showAllLessons
    ? selectedSection.lessons
    : selectedSection.lessons.slice(0, previewLessonCount);
  const completedCount = allLessons.filter(lesson =>
    completedLessonIds.has(lesson.id)
  ).length;
  const overallProgress = (completedCount / allLessons.length) * 100;
  const lessonPosition = readLessonPosition(nextCourseLesson.id);
  const continueLabel = lessonPosition > 15 ? "Continue lesson" : "Next lesson";
  const nextLessonAvailable = availableLessonIds.has(nextCourseLesson.id);
  const nextBuildStatus = buildStatuses[nextCourseLesson.id];

  const openLesson = (lesson: Lesson) => {
    const generated = generatedLessonIds.has(lesson.id);
    onOpenLesson(
      generated
        ? {
            ...lesson,
            pilot: true,
            audioMode: "sequence",
            manifestSrc: `/api/lessons/${lesson.id}/manifest`
          }
        : lesson
    );
  };

  const requestGeneration = (lessonId: number) => {
    if (
      activeGenerationId !== null &&
      activeGenerationId !== lessonId
    ) {
      return;
    }
    if (!hasGenerationAccess) {
      setPendingTokenLessonId(lessonId);
      return;
    }
    void onGenerateLesson(lessonId);
  };

  const buildLabel = (lessonId: number) => {
    const status = buildStatuses[lessonId];
    if (status?.state === "checking") return "Checking voices…";
    if (status?.state === "queued") return "Starting audio…";
    if (status?.state === "rendering") {
      const progress =
        status.total && status.completed !== undefined
          ? Math.round((status.completed / status.total) * 100)
          : null;
      return progress === null ? "Building audio…" : `Building audio ${progress}%`;
    }
    if (status?.state === "ready") return "Audio ready";
    if (status?.state === "error") return "Try generating again";
    if (activeGenerationId !== null && activeGenerationId !== lessonId) {
      return "Another lesson is building";
    }
    return "Generate audio";
  };

  const nextAction = () => {
    if (nextLessonAvailable) {
      openLesson(nextCourseLesson);
    } else {
      requestGeneration(nextCourseLesson.id);
    }
  };

  return (
    <main className="course-shell">
      <header className="site-header">
        <Brand />
        <div className="header-actions">
          <span className="streak">
            <Flame aria-hidden="true" />
            Start your streak
          </span>
          <span className="pilot-badge">5 sections · 150 lessons</span>
        </div>
      </header>

      <div className="course-main">
        <div className="course-intro">
          <section className="course-hero" aria-labelledby="course-heading">
            <p className="eyebrow">A complete Bangla speaking course</p>
            <h1 id="course-heading">Speak with family, one lesson at a time.</h1>
            <p className="hero-copy">
              Five sections take you from your first greeting to independent
              family conversation.
            </p>
          </section>

          <section className="overall-progress" aria-label="Course progress">
            <div className="overall-progress__heading">
              <div>
                <p className="eyebrow">Your progress</p>
                <strong>
                  {completedCount}
                  <span> / 150 lessons</span>
                </strong>
              </div>
              <span>{Math.round(overallProgress)}%</span>
            </div>
            <progress value={completedCount} max={allLessons.length} />
            <p>
              Earlier language returns naturally inside each new lesson.
            </p>
          </section>
        </div>

        <button
          type="button"
          className={`continue-row ${
            nextLessonAvailable ? "" : "continue-row--generate"
          }`}
          onClick={nextAction}
          disabled={
            !nextLessonAvailable &&
            activeGenerationId !== null &&
            activeGenerationId !== nextCourseLesson.id
          }
        >
          <span className="continue-row__play" aria-hidden="true">
            {nextLessonAvailable ? (
              <Play fill="currentColor" />
            ) : nextBuildStatus &&
              ["checking", "queued", "rendering"].includes(
                nextBuildStatus.state
              ) ? (
              <LoaderCircle className="spin" />
            ) : (
              <WandSparkles />
            )}
          </span>
          <span className="continue-row__body">
            <span className="continue-row__label">
              {nextLessonAvailable
                ? continueLabel
                : buildLabel(nextCourseLesson.id)}
            </span>
            <strong>
              Section {nextCourseLesson.sectionId} · Lesson{" "}
              {nextCourseLesson.id} — {nextCourseLesson.title}
            </strong>
            <span className="continue-row__meta">
              <Clock3 aria-hidden="true" /> 30 min
              {!nextLessonAvailable ? (
                <span>
                  {nextBuildStatus?.state === "error"
                    ? nextBuildStatus.message ?? "Audio build needs another try"
                    : "One tap creates the complete lesson"}
                </span>
              ) : lessonPosition > 15 ? (
                <span>Resume where you stopped</span>
              ) : (
                <span>Listen and answer out loud</span>
              )}
            </span>
          </span>
          <ChevronRight aria-hidden="true" />
        </button>

        <section className="course-roadmap" aria-labelledby="roadmap-heading">
          <div className="roadmap-heading">
            <div>
              <p className="eyebrow">Your course</p>
              <h2 id="roadmap-heading">Five sections. One clear path.</h2>
            </div>
            <p>Choose a section to see its 30 lessons.</p>
          </div>

          <div className="section-grid" aria-label="Course sections">
            {courseSections.map(section => {
              const sectionCompleted = section.lessons.filter(lesson =>
                completedLessonIds.has(lesson.id)
              ).length;
              const selected = section.id === selectedSection.id;
              return (
                <button
                  type="button"
                  className={`section-card section-card--${section.accent} ${
                    selected ? "section-card--selected" : ""
                  }`}
                  key={section.id}
                  onClick={() => {
                    setSelectedSectionId(section.id);
                    setShowAllLessons(false);
                  }}
                  aria-pressed={selected}
                >
                  <span className="section-card__number">
                    {String(section.id).padStart(2, "0")}
                  </span>
                  <span className="section-card__copy">
                    <strong>{section.title}</strong>
                    <span>{section.level}</span>
                  </span>
                  <span className="section-card__progress">
                    <span>{sectionCompleted} of 30</span>
                    <i aria-hidden="true">
                      <i
                        style={{
                          width: `${(sectionCompleted / section.lessons.length) * 100}%`
                        }}
                      />
                    </i>
                  </span>
                </button>
              );
            })}
          </div>

          <section
            className={`selected-section selected-section--${selectedSection.accent}`}
            aria-labelledby={`section-${selectedSection.id}-heading`}
          >
            <div className="selected-section__heading">
              <span className="selected-section__number">
                {String(selectedSection.id).padStart(2, "0")}
              </span>
              <div>
                <p className="eyebrow">Section {selectedSection.id}</p>
                <h3 id={`section-${selectedSection.id}-heading`}>
                  {selectedSection.title}
                </h3>
                <p>{selectedSection.outcome}</p>
              </div>
              <span className="selected-section__range">
                Lessons {selectedSection.lessons[0].id}–
                {selectedSection.lessons.at(-1)?.id}
              </span>
            </div>

            <div className="lesson-grid">
              {selectedLessons.map(lesson => {
                const generated = generatedLessonIds.has(lesson.id);
                const available = Boolean(lesson.pilot || generated);
                const complete = completedLessonIds.has(lesson.id);
                const buildStatus = buildStatuses[lesson.id];
                const building = Boolean(
                  buildStatus &&
                    ["checking", "queued", "rendering"].includes(
                      buildStatus.state
                    )
                );
                const blockedByOtherBuild =
                  activeGenerationId !== null &&
                  activeGenerationId !== lesson.id;
                return (
                  <article
                    className={`lesson-card ${
                      available ? "lesson-card--available" : ""
                    } ${complete ? "lesson-card--complete" : ""}`}
                    key={lesson.id}
                  >
                    <span className="lesson-card__number">
                      {complete ? <Check aria-hidden="true" /> : lesson.id}
                    </span>
                    <span className="lesson-card__copy">
                      <strong>{lesson.title}</strong>
                      <span>{lesson.canDo}</span>
                      {buildStatus?.state === "error" ? (
                        <span className="lesson-card__error">
                          {buildStatus.message ?? "Audio build needs another try."}
                        </span>
                      ) : null}
                    </span>
                    <button
                      className={`lesson-card__action ${
                        available ? "lesson-card__action--play" : ""
                      }`}
                      type="button"
                      onClick={() =>
                        available
                          ? openLesson(lesson)
                          : requestGeneration(lesson.id)
                      }
                      disabled={!available && (building || blockedByOtherBuild)}
                      aria-label={
                        available
                          ? `${complete ? "Replay" : "Play"} lesson ${lesson.id}`
                          : `${buildLabel(lesson.id)} for lesson ${lesson.id}`
                      }
                    >
                      {available ? (
                        <>
                          <Headphones aria-hidden="true" />
                          {complete ? "Replay" : "30 min"}
                        </>
                      ) : (
                        <>
                          {building ? (
                            <LoaderCircle className="spin" aria-hidden="true" />
                          ) : (
                            <WandSparkles aria-hidden="true" />
                          )}
                          {buildLabel(lesson.id)}
                        </>
                      )}
                    </button>
                  </article>
                );
              })}
            </div>

            {!showAllLessons ? (
              <button
                className="show-all"
                type="button"
                onClick={() => setShowAllLessons(true)}
              >
                View all 30 lessons in Section {selectedSection.id}
                <ChevronRight aria-hidden="true" />
              </button>
            ) : null}
          </section>
        </section>

        <footer className="course-note">
          <strong>150 lessons · 30 minutes each · approximately 75 hours</strong>
          <p>
            Progress is saved on this device. Completing a lesson advances the
            course automatically.
          </p>
        </footer>
      </div>

      {pendingTokenLessonId !== null ? (
        <div
          className="generation-gate"
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setPendingTokenLessonId(null);
            }
          }}
        >
          <form
            className="generation-gate__card"
            onSubmit={event => {
              event.preventDefault();
              if (!tokenInput.trim()) return;
              const lessonId = pendingTokenLessonId;
              setPendingTokenLessonId(null);
              void onGenerateLesson(lessonId, tokenInput);
              setTokenInput("");
            }}
          >
            <WandSparkles aria-hidden="true" />
            <p className="eyebrow">First build only</p>
            <h2>Connect lesson generation</h2>
            <p>
              Enter your private course key once. Shuni will remember it on
              this device, so every later lesson is one tap.
            </p>
            <label htmlFor="course-key">Private course key</label>
            <input
              id="course-key"
              type="password"
              autoComplete="current-password"
              value={tokenInput}
              onChange={event => setTokenInput(event.target.value)}
              autoFocus
            />
            <div className="generation-gate__actions">
              <button
                type="button"
                className="text-button"
                onClick={() => setPendingTokenLessonId(null)}
              >
                Cancel
              </button>
              <button type="submit" disabled={!tokenInput.trim()}>
                Save and generate
              </button>
            </div>
            <span>
              <CircleAlert aria-hidden="true" />
              This prevents anyone with the public site link from spending your
              ElevenLabs credits.
            </span>
          </form>
        </div>
      ) : null}
    </main>
  );
}
